import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import checkoutService from "../../services/checkoutService"; // Corrected: Use default import

// Non-serializable state is no longer managed here or needed externally
// const nonSerializableState = {
//   stripe: null,
//   elements: null,
//   cardElement: null,
// };

const initialState = {
  clientSecret: null,
  orderId: null,
  orderNumber: null,
  checkoutSession: null, // Stores response from createCheckoutSession
  activeStep: 0,
  paymentCompleted: false,
  loading: false,
  error: null,
  paymentInstructions: null, // Add initial state for manual bank instructions
  nextAction: null, // Add initial state for Stripe bank instructions
  checkoutData: {
    shippingAddress: {
      address: "",
      city: "",
      postalCode: "",
      country: "CH",
    },
    billingAddress: {
      address: "",
      city: "",
      postalCode: "",
      country: "CH",
      sameAsShipping: true,
    },
    paymentMethod: "credit_card", // Default payment method
    couponCode: "",
    shippingMethod: "standard", // Default shipping method
    shippingMethodId: null, // Store the ID of the selected shipping method
    amountToUseFromWallet: 0, // Ensure this field exists in initial state
  },
  prefillCompleted: false, // New flag to track if prefill has run
  // hasCardElement is removed - Stripe Elements provider will handle element readiness
};

// Async thunk for creating checkout session
export const createCheckoutSessionThunk = createAsyncThunk(
  "checkout/createSession",
  async (checkoutData, { rejectWithValue, getState }) => {
    try {
      console.log("createCheckoutSessionThunk: Data sent:", checkoutData);
      const response = await checkoutService.createCheckoutSession(
        checkoutData
      );
      console.log("createCheckoutSessionThunk: Response received:", response);
      return response; // Includes clientSecret, orderId, orderNumber, amountDue, paidByWalletOnly etc.
    } catch (error) {
      console.error("createCheckoutSessionThunk Error:", error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Async thunk for processing payment
export const processPaymentThunk = createAsyncThunk(
  "checkout/processPayment",
  async (paymentData, { rejectWithValue, getState }) => {
    const { orderId } = getState().checkout;
    if (!orderId) {
      return rejectWithValue("Order ID not found in state.");
    }
    try {
      console.log("processPaymentThunk: Data sent:", {
        ...paymentData,
        orderId,
      });
      const response = await checkoutService.processPayment(
        orderId,
        paymentData
      );
      console.log("processPaymentThunk: Response received:", response);
      return { ...response, orderId }; // Ensure orderId is part of success payload
    } catch (error) {
      console.error("processPaymentThunk Error:", error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const checkoutSlice = createSlice({
  name: "checkout",
  initialState,
  reducers: {
    setClientSecret: (state, action) => {
      state.clientSecret = action.payload;
    },
    setOrderId: (state, action) => {
      state.orderId = action.payload;
    },
    setOrderNumber: (state, action) => {
      state.orderNumber = action.payload;
    },
    setCheckoutSession: (state, action) => {
      state.checkoutSession = action.payload;
      // Auto-update related fields if present in the session payload
      if (action.payload?.clientSecret) {
        state.clientSecret = action.payload.clientSecret;
      }
      if (action.payload?.orderId) {
        state.orderId = action.payload.orderId;
      }
      if (action.payload?.orderNumber) {
        state.orderNumber = action.payload.orderNumber;
      }
      if (action.payload?.paymentInstructions !== undefined) {
        // Check if present, even if null
        state.paymentInstructions = action.payload.paymentInstructions;
      }
      if (action.payload?.nextAction !== undefined) {
        // Check if present, even if null
        state.nextAction = action.payload.nextAction;
      }
      // Set wallet amount used in the checkoutData
      if (action.payload?.amountPaidByWallet !== undefined) {
        state.checkoutData.amountToUseFromWallet = parseFloat(
          action.payload.amountPaidByWallet || 0
        );
      }
    },
    setActiveStep: (state, action) => {
      state.activeStep = action.payload;
    },
    setPaymentCompleted: (state, action) => {
      state.paymentCompleted = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    updateCheckoutField: (state, action) => {
      const { section, field, value } = action.payload;
      if (section) {
        // Ensure the section exists before trying to access its field
        if (state.checkoutData[section]) {
          state.checkoutData[section][field] = value;

          // ✅ FIX: Auto-sync billing address when shipping address changes and sameAsShipping is true
          if (
            section === "shippingAddress" &&
            state.checkoutData.billingAddress.sameAsShipping
          ) {
            // Copy the shipping address field to billing address
            state.checkoutData.billingAddress[field] = value;
            console.log(
              `Auto-synced billing.${field} = "${value}" because sameAsShipping is true`
            );
          }
        } else {
          console.warn(`Checkout section "${section}" not found.`);
        }
      } else {
        // Handle top-level fields like paymentMethod, couponCode, amountToUseFromWallet
        if (field in state.checkoutData || field === "amountToUseFromWallet") {
          // Directly set if it's a known field or the new wallet amount field
          state.checkoutData[field] = value;
        } else {
          console.warn(`Checkout field "${field}" not found.`);
        }
        // Ensure amountToUseFromWallet exists even if value is 0 or null
        if (
          field === "amountToUseFromWallet" &&
          !(field in state.checkoutData)
        ) {
          state.checkoutData.amountToUseFromWallet = value || 0;
        }
      }
    },
    setSameAsShipping: (state, action) => {
      const checked = action.payload;
      state.checkoutData.billingAddress.sameAsShipping = checked;

      if (checked) {
        // ✅ FIX: Always copy ALL shipping address details to billing address when checked
        const shipping = state.checkoutData.shippingAddress;
        state.checkoutData.billingAddress.address = shipping.address || "";
        state.checkoutData.billingAddress.city = shipping.city || "";
        state.checkoutData.billingAddress.postalCode =
          shipping.postalCode || "";
        state.checkoutData.billingAddress.country = shipping.country || "CH";

        console.log("setSameAsShipping: Copied shipping to billing:", {
          address: shipping.address,
          city: shipping.city,
          postalCode: shipping.postalCode,
          country: shipping.country,
        });
      } else {
        // When unchecked, keep existing billing address data (don't clear it)
        console.log(
          "setSameAsShipping: Unchecked - keeping existing billing address"
        );
      }
    },
    // setHasCardElement removed
    resetCheckout: (state) => {
      // Preserve the current country selections before resetting
      const defaultShippingCountry =
        state.checkoutData.shippingAddress.country ||
        initialState.checkoutData.shippingAddress.country;
      const defaultBillingCountry =
        state.checkoutData.billingAddress.country ||
        initialState.checkoutData.billingAddress.country;

      // Reset the entire state to the initial state structure
      // This creates a fresh copy that Immer can work with
      Object.assign(state, JSON.parse(JSON.stringify(initialState)));

      // Now, safely set the preserved defaults onto the new draft state
      state.checkoutData.shippingAddress.country = defaultShippingCountry;
      state.checkoutData.billingAddress.country = defaultBillingCountry;

      // Ensure other critical defaults are set correctly after reset
      state.activeStep = 0;
      state.prefillCompleted = false;
      state.checkoutData.billingAddress.sameAsShipping = true;
    },
    // Action to clear specific sensitive data after completion/failure
    clearSensitiveData: (state) => {
      state.clientSecret = null;
      // Potentially clear other temporary data
    },
    // New action to mark prefill as completed
    setPrefillCompleted: (state, action) => {
      state.prefillCompleted = action.payload; // Set based on payload (usually true)
    },
  },
  extraReducers: (builder) => {
    builder
      // === Create Checkout Session ===
      .addCase(createCheckoutSessionThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.clientSecret = null;
        state.orderId = null;
        state.orderNumber = null;
        state.paymentCompleted = false; // Reset payment status
        state.checkoutSession = null; // Clear previous session data
      })
      .addCase(createCheckoutSessionThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.clientSecret = action.payload.clientSecret;
        state.orderId = action.payload.orderId;
        state.orderNumber = action.payload.orderNumber;
        state.checkoutSession = action.payload; // Store the whole session response

        // Determine next step based on backend response
        if (action.payload.paidByWalletOnly) {
          state.activeStep = 2; // Skip payment step if fully paid by wallet
          state.paymentCompleted = true;
          console.log(
            "Checkout Slice: Order fully paid by wallet, moving to step 2"
          );
        } else if (
          action.payload.clientSecret ||
          action.payload.paymentMethod === "bank_transfer_manual"
        ) {
          // Only advance if a client secret is available (for Stripe card/other methods)
          // OR if it's a manual bank transfer (which doesn't need a client secret)
          state.activeStep = 1;
          console.log("Checkout Slice: Proceeding to payment step 1");
        } else {
          // Handle case where neither condition is met (shouldn't happen with current logic)
          state.error = "Could not determine next checkout step.";
          console.error(
            "Checkout Slice: Invalid state after create session - no client secret/wallet payment/manual transfer."
          );
        }
      })
      .addCase(createCheckoutSessionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create checkout session.";
        state.activeStep = 0; // Stay on address step on failure
      })
      // === Process Payment ===
      .addCase(processPaymentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.paymentCompleted = false; // Reset payment status
      })
      .addCase(processPaymentThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentCompleted = true;
        state.activeStep = 2; // Move to confirmation step
        state.orderId = action.payload.orderId; // Ensure orderId is set from payload
        // Clear client secret after successful payment
        state.clientSecret = null;
        console.log(
          "Checkout Slice: Payment processed successfully, moving to step 2"
        );
      })
      .addCase(processPaymentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Payment failed.";
        state.paymentCompleted = false;
        // Do not change step, stay on payment step
        console.error(
          "Checkout Slice: Payment processing failed:",
          action.payload
        );
      });
  },
});

export const {
  setClientSecret,
  setOrderId,
  setOrderNumber,
  setCheckoutSession,
  setActiveStep,
  setPaymentCompleted,
  setLoading,
  setError,
  updateCheckoutField,
  setSameAsShipping,
  // setHasCardElement removed
  resetCheckout,
  clearSensitiveData,
  setPrefillCompleted,
} = checkoutSlice.actions;

// Non-serializable action handlers and getters are removed
// export const setStripeInstance = ...
// export const setStripeElements = ...
// export const setCardElementInstance = ...
// export const getStripeInstance = ...
// export const getStripeElements = ...
// export const getCardElementInstance = ...

export default checkoutSlice.reducer;
